// admin/src/pages/ApiIntegrationMonitoringPage.jsx - API 연계 모니터링 페이지
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { collection, query, orderBy, getDocs, limit, startAfter, where } from 'firebase/firestore';
import {
  ServerIcon,
  CloudIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import { Table, Tag, Divider } from 'antd';

const ApiIntegrationMonitoringPage = () => {
  const [remittances, setRemittances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRemittance, setSelectedRemittance] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null);
  const [pageDocs, setPageDocs] = useState([]); // 각 페이지의 마지막 문서 저장
  
  // 전체 통계 (필터 무관) - 변경된 프로세스: WAITING_DEPOSIT 제거
  const [stats, setStats] = useState({
    total: 0,
    processing: 0,
    uabProcessing: 0,
    completed: 0,
    failed: 0
  });

  // 초기 통계 로드
  useEffect(() => {
    loadAllStats();
  }, []);

  // 데이터 로드
  useEffect(() => {
    loadRemittances();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadRemittances();
        loadAllStats(); // 통계도 함께 업데이트
      }, 5 * 60 * 1000); // 5분마다 새로고침
      return () => clearInterval(interval);
    }
  }, [autoRefresh, currentPage, filter]);

  // 실시간 업데이트를 위한 WebSocket 연결 (선택사항)
  useEffect(() => {
    // WebSocket 연결이 필요한 경우 여기에 구현
    // 현재는 polling 방식으로 구현
  }, []);

  const loadRemittances = async () => {
    try {
      setLoading(true);
      
      // Firebase에서 송금 데이터 직접 조회 (페이지네이션 적용)
      const remittancesRef = collection(db, `artifacts/${appId}/remittances`);
      
      // 필터 쿼리 구성
      let baseQuery = query(remittancesRef, orderBy('createdAt', 'desc'));
      
      // 필터 적용
      if (filter !== 'ALL') {
        baseQuery = query(baseQuery, where('status', '==', filter));
      }
      
      // 페이지네이션을 위한 쿼리 구성
      let paginatedQuery = baseQuery;
      let lastDoc = null;
      let remittancesData = [];
      
      // 현재 페이지의 시작점 찾기
      if (currentPage === 1) {
        // 첫 페이지
        paginatedQuery = query(baseQuery, limit(pageSize));
      } else {
        // 이전 페이지의 마지막 문서 찾기
        // pageDocs 배열에서 바로 이전 페이지의 문서를 사용하거나, 없으면 순차적으로 계산
        let startDoc = null;
        
        // 바로 이전 페이지의 문서가 있으면 사용
        if (pageDocs[currentPage - 2]) {
          startDoc = pageDocs[currentPage - 2];
        } else {
          // 없으면 첫 페이지부터 순차적으로 계산하여 필요한 페이지까지 도달
          let currentLastDoc = null;
          for (let page = 1; page < currentPage; page++) {
            let pageQuery;
            if (page === 1) {
              pageQuery = query(baseQuery, limit(pageSize));
            } else if (currentLastDoc) {
              pageQuery = query(baseQuery, startAfter(currentLastDoc), limit(pageSize));
            } else {
              break;
            }
            
            const pageSnapshot = await getDocs(pageQuery);
            const pageDocsArray = [];
            pageSnapshot.forEach(doc => {
              pageDocsArray.push(doc);
              currentLastDoc = doc;
            });
            
            // 페이지 문서 저장
            if (currentLastDoc && pageDocsArray.length > 0) {
              const newPageDocs = [...pageDocs];
              newPageDocs[page - 1] = currentLastDoc;
              setPageDocs(newPageDocs);
            }
            
            if (pageDocsArray.length < pageSize) break;
          }
          startDoc = currentLastDoc;
        }
        
        if (startDoc) {
          paginatedQuery = query(baseQuery, startAfter(startDoc), limit(pageSize));
        } else {
          // 시작 문서를 찾을 수 없으면 첫 페이지로
          paginatedQuery = query(baseQuery, limit(pageSize));
        }
      }
      
      // 현재 페이지 데이터 가져오기
      const snapshot = await getDocs(paginatedQuery);
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const remittanceData = {
          id: doc.id,
          remitId: data.remitId,
          ...data,
          // Timestamp 객체를 ISO 문자열로 변환
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
          completedAt: data.completedAt?.toDate?.()?.toISOString() || data.completedAt,
          providerDepositConfirmedAt: data.providerDepositConfirmedAt?.toDate?.()?.toISOString() || data.providerDepositConfirmedAt,
          uabCompletedAt: data.uabCompletedAt?.toDate?.()?.toISOString() || data.uabCompletedAt,
          settledAt: data.settledAt?.toDate?.()?.toISOString() || data.settledAt,
          // rates 객체의 appliedAt도 변환
          rates: data.rates ? {
            ...data.rates,
            appliedAt: data.rates.appliedAt?.toDate?.()?.toISOString() || data.rates.appliedAt
          } : null,
          // events 배열의 timestamp도 변환
          events: data.events?.map(event => ({
            ...event,
            timestamp: event.timestamp?.toDate?.()?.toISOString() || event.timestamp
          })) || []
        };
        remittancesData.push(remittanceData);
        lastDoc = doc;
      });
      
      // 현재 페이지의 마지막 문서 저장
      if (lastDoc && remittancesData.length > 0) {
        const newPageDocs = [...pageDocs];
        newPageDocs[currentPage - 1] = lastDoc;
        setPageDocs(newPageDocs);
      }
      
      // 전체 개수는 별도로 조회 (첫 페이지일 때만)
      if (currentPage === 1) {
        const countSnapshot = await getDocs(baseQuery);
        setTotalCount(countSnapshot.size);
        
        // 전체 통계 조회 (필터 무관)
        await loadAllStats();
      }
      
      setRemittances(remittancesData);
      setLastVisibleDoc(lastDoc);
    } catch (error) {
      console.error('Failed to load remittances:', error);
    } finally {
      setLoading(false);
    }
  };

  // 전체 통계 로드 (필터 무관)
  const loadAllStats = async () => {
    try {
      const remittancesRef = collection(db, `artifacts/${appId}/remittances`);
      const allQuery = query(remittancesRef, orderBy('createdAt', 'desc'));
      const allSnapshot = await getDocs(allQuery);
      
      const allRemittances = [];
      allSnapshot.forEach(doc => {
        allRemittances.push(doc.data());
      });
      
      const newStats = {
        total: allRemittances.length,
        // 🔥 변경된 프로세스: WAITING_DEPOSIT 제거
        processing: allRemittances.filter(r => r.status === 'PROCESSING').length,
        uabProcessing: allRemittances.filter(r => r.status === 'UAB_PROCESSING').length,
        completed: allRemittances.filter(r => r.status === 'COMPLETED').length,
        failed: allRemittances.filter(r => r.status === 'FAILED').length
      };
      
      console.log('📊 전체 통계:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('전체 통계 조회 실패:', error);
    }
  };

  // 필터 변경 시 첫 페이지로 리셋
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    setPageDocs([]); // 페이지 문서 캐시 초기화
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 상태별 아이콘 및 색상 (변경된 프로세스: WAITING_DEPOSIT, PENDING 제거)
  const getStatusInfo = (status) => {
    switch (status) {
      case 'PROCESSING':
        return { icon: ArrowPathIcon, color: 'text-purple-600', bgColor: 'bg-purple-100' };
      case 'UAB_PROCESSING':
        return { icon: ArrowPathIcon, color: 'text-blue-600', bgColor: 'bg-blue-100' };
      case 'COMPLETED':
        return { icon: CheckCircleIcon, color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'FAILED':
        return { icon: XCircleIcon, color: 'text-red-600', bgColor: 'bg-red-100' };
      default:
        return { icon: ExclamationTriangleIcon, color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
  };

  // API 단계별 상태 확인 (변경된 프로세스)
  const getApiStepStatus = (remittance) => {
    const hectoPayoutAt = remittance.jeonbukUabCompletedAt || remittance.nonghyupUabCompletedAt;
    const hectoTxn = remittance.jeonbukTransactionId || remittance.nonghyupTransactionId;
    const steps = {
      hectoPayout: {
        name: '헥토 해외송금',
        status: hectoPayoutAt ? 'success' : (remittance.status === 'PROCESSING' ? 'processing' : 'pending'),
        data: { completedAt: hectoPayoutAt, transactionId: hectoTxn }
      },
      receivingBankTransfer: {
        name: '수취은행 송금 지시',
        status: remittance.uabRequestId ? 'success' : (remittance.status === 'UAB_PROCESSING' ? 'processing' : 'pending'),
        data: { requestId: remittance.uabRequestId }
      },
      payoutConfirmation: {
        name: '해외송금 완료',
        status: remittance.uabCompletedAt ? 'success' : 'pending',
        data: { completedAt: remittance.uabCompletedAt, transactionId: remittance.uabTransactionId }
      }
    };
    return steps;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 p-6">
      {/* 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                <CpuChipIcon className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold">API 연계 모니터링</h1>
                <p className="text-blue-100 text-lg mt-2">
                  송금업체 및 헥토 파트너 레일 연계 상태 (5분 단위, UAB 직접 API 없음)
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm">5분 단위 모니터링</span>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="autoRefresh"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="autoRefresh" className="text-sm">자동 새로고침</label>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  loadRemittances();
                  loadAllStats();
                }}
                className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-300 flex items-center space-x-2"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>새로고침</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 통계 카드 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8"
      >
        {[
          { label: '전체 송금', count: stats.total, icon: ChartBarIcon, color: 'blue' },
          { label: '헥토 해외송금 처리 중', count: stats.processing, icon: ArrowPathIcon, color: 'purple' },
          { label: '수취은행 처리 중', count: stats.uabProcessing, icon: ArrowPathIcon, color: 'blue' },
          { label: '완료', count: stats.completed, icon: CheckCircleIcon, color: 'green' },
          { label: '실패', count: stats.failed, icon: XCircleIcon, color: 'red' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            whileHover={{ y: -5, scale: 1.02 }}
            className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.count}</p>
              </div>
              <div className={`p-3 bg-${stat.color}-100 rounded-xl`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* 필터 및 송금 목록 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
      >
        {/* 필터 헤더 */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 px-6 py-4 border-b border-gray-200/50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
              <ServerIcon className="w-5 h-5" />
              <span>송금 API 연계 현황</span>
            </h2>
            <div className="flex space-x-2">
              {['ALL', 'PROCESSING', 'UAB_PROCESSING', 'COMPLETED', 'FAILED'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleFilterChange(status)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-300 ${
                    filter === status
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status === 'ALL' ? '전체' : 
                   status === 'PROCESSING' ? '헥토 해외송금 처리 중' :
                   status === 'UAB_PROCESSING' ? '수취은행 송금 처리 중' :
                   status === 'COMPLETED' ? '완료' : '실패'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 송금 목록 */}
        <div className="overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">데이터 로딩 중...</span>
            </div>
          ) : remittances.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
              <span className="ml-2 text-gray-600">송금 데이터가 없습니다</span>
            </div>
          ) : (
            remittances.map((remittance, index) => {
              const statusInfo = getStatusInfo(remittance.status);
              const apiSteps = getApiStepStatus(remittance);
              
              return (
                <motion.div
                  key={remittance.remitId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-200/50 last:border-b-0 hover:bg-gray-50/50 transition-all duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      {/* 송금 정보 */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-3">
                          <div className={`px-3 py-1 rounded-lg text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color} flex items-center space-x-1`}>
                            <statusInfo.icon className="w-4 h-4" />
                            <span>{remittance.status}</span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(remittance.createdAt?.toDate?.() || remittance.createdAt).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500">송금 ID</p>
                            <p className="font-mono text-sm">{remittance.remitId}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">금액</p>
                            <p className="font-semibold">{remittance.amountKRW?.toLocaleString()} KRW</p>
                            <p className="text-sm text-gray-600">${remittance.amountUSD} USD</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">수취인</p>
                            <p className="font-semibold">{remittance.receiver?.name}</p>
                            <p className="text-sm text-gray-600">{remittance.receiver?.bank}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">처리 시간</p>
                            <p className="font-semibold">
                              {remittance.uabCompletedAt ? 
                                Math.round((new Date(remittance.uabCompletedAt) - new Date(remittance.createdAt)) / 1000) + '초' :
                                Math.round((new Date() - new Date(remittance.createdAt)) / 1000) + '초'
                              }
                            </p>
                          </div>
                        </div>

                        {/* API 단계별 상태 */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(apiSteps).map(([key, step]) => (
                            <div key={key} className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${
                                step.status === 'success' ? 'bg-green-500' : 
                                step.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                              }`}></div>
                              <span className="text-xs text-gray-600">{step.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 상세보기 버튼 */}
                      <div className="ml-4">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedRemittance(remittance);
                            setShowDetails(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center space-x-2"
                        >
                          <EyeIcon className="w-4 h-4" />
                          <span>상세보기</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
        
        {/* 페이지네이션 */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 px-6 py-4 border-t border-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {totalCount}개 중 {((currentPage - 1) * pageSize) + 1}~{Math.min(currentPage * pageSize, totalCount)}개 표시
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
                  currentPage === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                이전
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(Math.ceil(totalCount / pageSize), 10) }, (_, i) => i + 1)
                  .filter(page => {
                    // 현재 페이지 기준으로 앞뒤 2페이지만 표시
                    return page === 1 || 
                           page === Math.min(Math.ceil(totalCount / pageSize), 10) ||
                           Math.abs(page - currentPage) <= 2;
                  })
                  .map((page, idx, arr) => {
                    // 페이지 번호 사이에 ... 표시
                    const showEllipsis = idx > 0 && arr[idx] - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
                            currentPage === page
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalCount / pageSize) || loading || currentPage >= 10}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
                  currentPage >= Math.ceil(totalCount / pageSize) || loading || currentPage >= 10
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                다음
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 상세보기 모달 */}
      {showDetails && selectedRemittance && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">송금 상세 정보</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[calc(90vh-80px)] overflow-y-auto">
              {/* 송금업체 요청 */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <ServerIcon className="w-5 h-5 text-blue-600" />
                  <span>1. 송금업체 요청</span>
                  <Tag color={selectedRemittance.providerRequestId ? 'green' : 'orange'}>
                    {selectedRemittance.providerRequestId ? '완료' : '대기'}
                  </Tag>
                </h4>
                <Table
                  dataSource={[
                    { key: 'remitId', label: '송금 ID', value: selectedRemittance.remitId || 'N/A' },
                    { key: 'requestId', label: '요청 ID', value: selectedRemittance.providerRequestId || selectedRemittance.requestId || 'N/A' },
                    { key: 'requestedAt', label: '요청 시간', value: selectedRemittance.createdAt ? (typeof selectedRemittance.createdAt === 'string' ? new Date(selectedRemittance.createdAt).toLocaleString() : selectedRemittance.createdAt) : 'N/A' },
                    { key: 'amountKRW', label: '송금액 (KRW)', value: selectedRemittance.amountKRW ? selectedRemittance.amountKRW.toLocaleString() + ' 원' : 'N/A' },
                    { key: 'amountUSD', label: '송금액 (USD)', value: selectedRemittance.amountUSD ? '$' + selectedRemittance.amountUSD.toLocaleString() : 'N/A' },
                    { key: 'providerResponse', label: '응답 데이터', value: selectedRemittance.providerResponse ? JSON.stringify(selectedRemittance.providerResponse, null, 2) : 'N/A' }
                  ].filter(item => item.value !== 'N/A' || item.key === 'providerResponse')}
                  columns={[
                    { title: '항목', dataIndex: 'label', key: 'label', width: 150 },
                    { title: '값', dataIndex: 'value', key: 'value', render: (text) => <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded">{text}</pre> }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>

              <Divider />

              {/* 입금확인 */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span>2. 입금 확인</span>
                  <Tag color={selectedRemittance.providerDepositConfirmedAt ? 'green' : 'orange'}>
                    {selectedRemittance.providerDepositConfirmedAt ? '완료' : '대기'}
                  </Tag>
                </h4>
                <Table
                  dataSource={[
                    { key: 'confirmedAt', label: '확인 시간', value: selectedRemittance.providerDepositConfirmedAt ? (typeof selectedRemittance.providerDepositConfirmedAt === 'string' ? new Date(selectedRemittance.providerDepositConfirmedAt).toLocaleString() : selectedRemittance.providerDepositConfirmedAt) : 'N/A' },
                    { key: 'actualAmountKRW', label: '실제 입금액 (KRW)', value: selectedRemittance.actualAmountKRW ? selectedRemittance.actualAmountKRW.toLocaleString() + ' 원' : selectedRemittance.totalDepositKRW ? selectedRemittance.totalDepositKRW.toLocaleString() + ' 원' : 'N/A' },
                    { key: 'actualAmountUSD', label: '실제 입금액 (USD)', value: selectedRemittance.actualAmountUSD ? '$' + selectedRemittance.actualAmountUSD.toLocaleString() : 'N/A' },
                    { key: 'status', label: '상태', value: selectedRemittance.status || 'N/A' }
                  ].filter(item => item.value !== 'N/A' || item.key === 'status')}
                  columns={[
                    { title: '항목', dataIndex: 'label', key: 'label', width: 150 },
                    { title: '값', dataIndex: 'value', key: 'value', render: (text) => <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded">{text}</pre> }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>

              <Divider />

              {/* UAB 송금 */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <BanknotesIcon className="w-5 h-5 text-purple-600" />
                  <span>3. UAB 송금</span>
                  <Tag color={selectedRemittance.uabRequestId ? 'green' : 'orange'}>
                    {selectedRemittance.uabRequestId ? '완료' : '대기'}
                  </Tag>
                </h4>
                <Table
                  dataSource={[
                    { key: 'uabRequestId', label: 'UAB 요청 ID', value: selectedRemittance.uabRequestId || 'N/A' },
                    { key: 'uabTransactionId', label: 'UAB 거래 ID', value: selectedRemittance.uabTransactionId || 'N/A' },
                    { key: 'amountMMK', label: 'MMK 금액', value: selectedRemittance.amountMMK ? selectedRemittance.amountMMK.toLocaleString() + ' MMK' : selectedRemittance.actualAmountMMK ? selectedRemittance.actualAmountMMK.toLocaleString() + ' MMK' : 'N/A' },
                    { key: 'uabStatus', label: 'UAB 상태', value: selectedRemittance.uabStatus || selectedRemittance.status || 'N/A' },
                    { key: 'uabMessage', label: 'UAB 메시지', value: selectedRemittance.uabMessage || 'N/A' },
                    { key: 'receiver', label: '수취인 정보', value: selectedRemittance.receiver ? JSON.stringify(selectedRemittance.receiver, null, 2) : selectedRemittance.beneficiary ? JSON.stringify(selectedRemittance.beneficiary, null, 2) : 'N/A' }
                  ].filter(item => item.value !== 'N/A' || ['uabRequestId', 'uabTransactionId', 'receiver'].includes(item.key))}
                  columns={[
                    { title: '항목', dataIndex: 'label', key: 'label', width: 150 },
                    { title: '값', dataIndex: 'value', key: 'value', render: (text) => <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded">{text}</pre> }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>

              <Divider />

              {/* UAB 확인 */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600" />
                  <span>4. UAB 확인</span>
                  <Tag color={selectedRemittance.uabCompletedAt ? 'green' : 'orange'}>
                    {selectedRemittance.uabCompletedAt ? '완료' : '대기'}
                  </Tag>
                </h4>
                <Table
                  dataSource={[
                    { key: 'completedAt', label: '완료 시간', value: selectedRemittance.uabCompletedAt ? (typeof selectedRemittance.uabCompletedAt === 'string' ? new Date(selectedRemittance.uabCompletedAt).toLocaleString() : selectedRemittance.uabCompletedAt) : selectedRemittance.completedAt ? (typeof selectedRemittance.completedAt === 'string' ? new Date(selectedRemittance.completedAt).toLocaleString() : selectedRemittance.completedAt) : 'N/A' },
                    { key: 'uabTransactionId', label: 'UAB 거래 ID', value: selectedRemittance.uabTransactionId || 'N/A' },
                    { key: 'actualAmountMMK', label: '실제 송금액 (MMK)', value: selectedRemittance.actualAmountMMK ? selectedRemittance.actualAmountMMK.toLocaleString() + ' MMK' : selectedRemittance.amountMMK ? selectedRemittance.amountMMK.toLocaleString() + ' MMK' : 'N/A' },
                    { key: 'transferResult', label: '송금 결과', value: selectedRemittance.transferResult ? JSON.stringify(selectedRemittance.transferResult, null, 2) : 'N/A' },
                    { key: 'uabProcessedAt', label: '처리 시간', value: selectedRemittance.uabProcessedAt ? (typeof selectedRemittance.uabProcessedAt === 'string' ? new Date(selectedRemittance.uabProcessedAt).toLocaleString() : selectedRemittance.uabProcessedAt) : 'N/A' },
                    { key: 'estimatedDelivery', label: '예상 완료 시간', value: selectedRemittance.estimatedDelivery ? (typeof selectedRemittance.estimatedDelivery === 'string' ? new Date(selectedRemittance.estimatedDelivery).toLocaleString() : selectedRemittance.estimatedDelivery) : 'N/A' },
                    { key: 'uabFees', label: 'UAB 수수료', value: selectedRemittance.uabFees ? JSON.stringify(selectedRemittance.uabFees, null, 2) : 'N/A' },
                    { key: 'finalExchangeRate', label: '최종 환율', value: selectedRemittance.finalExchangeRate ? JSON.stringify(selectedRemittance.finalExchangeRate, null, 2) : 'N/A' }
                  ].filter(item => item.value !== 'N/A' || ['completedAt', 'uabTransactionId'].includes(item.key))}
                  columns={[
                    { title: '항목', dataIndex: 'label', key: 'label', width: 150 },
                    { title: '값', dataIndex: 'value', key: 'value', render: (text) => <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded">{text}</pre> }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ApiIntegrationMonitoringPage;
